class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.initRhovive(); // Rhovive
  }

  // Rhovive (DESIGN-SYSTEM.md §8.15, snippets/cart-drawer.liquid): the COPY-DECK error line for Dawn's
  // generic cart error, one-tap adds ([data-rv-drawer-add]), the per-line plan toggle ([data-rv-line-plan])
  // and closing the drawer when a same-page link inside it is followed.
  initRhovive() {
    if (this.dataset.rvError && window.cartStrings) window.cartStrings.error = this.dataset.rvError;

    this.addEventListener('click', (event) => {
      const add = event.target.closest('[data-rv-drawer-add]');
      if (add && this.contains(add)) {
        event.preventDefault();
        this.rvAdd(add);
        return;
      }
      const link = event.target.closest('a[href*="#"]');
      if (!link || !this.contains(link)) return;
      try {
        if (new URL(link.href, window.location.href).pathname === window.location.pathname) this.close();
      } catch (e) {}
    });

    // Capture phase: the plan radios must never reach CartItems' quantity "change" handler.
    this.addEventListener(
      'change',
      (event) => {
        const group = event.target.closest('[data-rv-line-plan]');
        if (!group) return;
        event.stopPropagation();
        this.rvPlan(group, event.target.value);
      },
      true
    );

    // Focus trap guards (see rvEnsureTrap).
    this.addEventListener('focusin', (event) => {
      const guard = event.target;
      const edge = guard && guard.dataset ? guard.dataset.rvTrap : '';
      if (!edge) return;
      event.stopPropagation(); // Dawn's document-level trap never sees a guard
      if (this.rvPass && edge === 'end') return; // parked for the Shift+Tab below
      if (this.classList.contains('active')) this.rvFocusEdge(edge === 'end' ? 'first' : 'last', guard);
    });

    // Shift+Tab from the first stop (or the panel itself): park focus on the end guard and let the browser's
    // own Shift+Tab run from there. It then lands on the real last stop, even one inside a closed shadow root
    // that script can't focus (Shopify's Google Pay button). If the browser doesn't move (another handler
    // cancelled the key), the start-guard logic runs instead.
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab' || !event.shiftKey || event.defaultPrevented) return;
      if (!this.classList.contains('active')) return;
      const inner = this.querySelector('.drawer__inner');
      const end = inner && inner.nextElementSibling;
      if (!end || !end.dataset || end.dataset.rvTrap !== 'end') return;
      const active = document.activeElement;
      if (active !== inner && active !== this.rvFirstStop(inner)) return;
      this.rvPass = true;
      end.focus();
      setTimeout(() => {
        this.rvPass = false;
        if (document.activeElement === end) this.rvFocusEdge('last', end);
      });
    });
    this.rvEnsureTrap();
  }

  // The first stop a Tab from the top of the panel reaches (the Close button).
  rvFirstStop(inner) {
    return (
      this.rvTabCandidates(inner).find(
        (el) => !el.localName.includes('-') && el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden'
      ) || null
    );
  }

  // Dawn's trapFocus() caches its first and last elements when the drawer opens, from the light DOM only.
  // Shopify's express buttons (content_for_additional_checkout_buttons) render late, partly in shadow DOM and
  // iframes, so the real last stop is never in that list and Tab walked out of the aria-modal dialog. Two
  // focus guards around .drawer__inner fix it without knowing the buttons: Tab past the last stop lands on
  // the end guard, which moves focus to the first stop. Shift+Tab from the first stop (or the panel) is
  // parked on the end guard (keydown handler above), so the browser itself steps back to the last stop; if
  // that fails, the start guard is the fallback and moves focus to the last stop script can focus. They sit
  // in #CartDrawer, outside .drawer__inner, so Dawn's CartItems re-render keeps them; renderContents() and
  // open() re-add them after a full re-render. .drawer is visibility:hidden while closed, so they are never
  // tab stops then.
  rvEnsureTrap() {
    const inner = this.querySelector('.drawer__inner');
    if (!inner) return;
    const guard = (edge) => {
      const el = document.createElement('span');
      el.className = 'rv-drawer__trap';
      el.tabIndex = 0;
      el.dataset.rvTrap = edge;
      el.style.cssText =
        'position:fixed;top:0;left:0;width:1px;height:1px;overflow:hidden;clip-path:inset(50%);outline:0;';
      return el;
    };
    const before = inner.previousElementSibling;
    const after = inner.nextElementSibling;
    if (!before || before.dataset.rvTrap !== 'start') inner.before(guard('start'));
    if (!after || after.dataset.rvTrap !== 'end') inner.after(guard('end'));
  }

  // Every element in the panel that could be a tab stop, in document order, descending into open shadow
  // roots. Custom elements are included because a closed shadow host with delegatesFocus takes focus too.
  rvTabCandidates(root) {
    const NATIVE =
      'a[href], area[href], button, input:not([type="hidden"]), select, textarea, summary, iframe, object, embed, [tabindex], [contenteditable]:not([contenteditable="false"])';
    const out = [];
    const walk = (node) => {
      node.querySelectorAll('*').forEach((el) => {
        if (el.dataset && el.dataset.rvTrap) return;
        const custom = el.localName.includes('-');
        if (el.matches(NATIVE) || custom) {
          const skip =
            el.disabled ||
            (el.hasAttribute('tabindex') && el.tabIndex < 0) ||
            (!custom && el.tabIndex < 0) ||
            (el.type === 'radio' &&
              !el.checked &&
              el.name &&
              node.querySelector(`input[type="radio"][name="${CSS.escape(el.name)}"]:checked`));
          if (!skip) out.push(el);
        }
        if (el.shadowRoot) walk(el.shadowRoot);
      });
    };
    walk(root);
    return out;
  }

  // Moves focus to the first or last stop that actually takes focus: hidden, inert and not-yet-rendered
  // elements refuse focus(), so trying them in order skips them without measuring anything.
  rvFocusEdge(which, from) {
    const inner = this.querySelector('.drawer__inner');
    if (!inner) return;
    const list = this.rvTabCandidates(inner);
    if (which === 'last') list.reverse();
    for (const el of list) {
      el.focus();
      const active = document.activeElement;
      if (active && active !== from && active !== document.body && inner.contains(active)) return;
    }
    inner.focus();
  }

  rvRefocus() {
    setTimeout(() => {
      const inner = this.querySelector('.drawer__inner');
      const container = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      if (inner && container) trapFocus(container, inner);
    }, 50);
  }

  rvAdd(button) {
    if (button.getAttribute('aria-busy') === 'true') return;
    const id = parseInt(button.dataset.rvDrawerAdd, 10);
    if (!id) return;
    const label = button.querySelector('[data-rv-label]');
    const labelHTML = label ? label.innerHTML : '';
    const scope = button.closest('.rv-upsell, .rv-empty') || this;
    // the multi-buy upsell has two add buttons: one add at a time
    if (scope.querySelector('[data-rv-drawer-add][aria-busy="true"]')) return;
    const error = scope.querySelector('[data-rv-drawer-error]');
    if (error) error.hidden = true;

    button.style.minWidth = `${button.offsetWidth}px`;
    button.setAttribute('aria-busy', 'true');
    if (label) label.textContent = this.dataset.rvAdding || 'Adding…';

    // Dawn 16's storefront standard events, as in product-form.js
    const SE = window.StandardEvents || {};
    const lines = SE.CartLinesUpdateEvent ? SE.CartLinesUpdateEvent.createPromise() : null;
    if (lines) {
      button.dispatchEvent(
        new SE.CartLinesUpdateEvent({
          action: 'add',
          context: 'dialog',
          lines: [{ merchandiseId: String(id), quantity: 1 }],
          promise: lines.promise,
        })
      );
    }

    fetch(`${routes.cart_add_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({
        items: [{ id, quantity: 1 }],
        sections: this.getSectionsToRender().map((section) => section.id),
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => response.json().then((json) => ({ ok: response.ok, json })))
      .then(({ ok, json }) => {
        if (!ok || json.status) {
          const e = new Error(json.description || json.message || '');
          e.shopify = Boolean(json.description);
          throw e;
        }
        if (lines && typeof CartItems !== 'undefined') {
          CartItems.fetchCartData()
            .then((cart) => {
              if (!cart || !cart.currency) return lines.reject(new Error('Missing currency in cart response'));
              lines.resolve({ cart: SE.CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
            })
            .catch((e) => lines.reject(e));
        }
        document.dispatchEvent(new CustomEvent('rv:cart-add', { detail: { items: json.items || [], source: this } }));
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'rv-drawer', cartData: json });
        }
        this.renderContents(json);
        this.rvRefocus();
      })
      .catch((e) => {
        if (lines) lines.reject(e);
        const message = e.shopify ? e.message : this.dataset.rvAddError || window.cartStrings.error;
        if (error) {
          const text = error.querySelector('[data-rv-drawer-error-text]');
          if (text) text.textContent = message;
          error.hidden = false;
        }
        if (SE.CartErrorEvent) {
          button.dispatchEvent(new SE.CartErrorEvent({ error: e.message || 'Network error', code: e.shopify ? 'INVALID' : 'SERVICE_UNAVAILABLE' }));
        }
        button.removeAttribute('aria-busy');
        button.style.minWidth = '';
        if (label) label.innerHTML = labelHTML;
      });
  }

  rvPlan(group, planId) {
    const line = parseInt(group.dataset.line, 10);
    if (!line) return;
    const quantity = parseInt(group.dataset.quantity, 10) || 1;
    group.setAttribute('aria-busy', 'true');
    group.querySelectorAll('input, select').forEach((input) => (input.disabled = true));

    fetch(`${routes.cart_change_url}`, {
      ...fetchConfig(),
      body: JSON.stringify({
        line,
        quantity,
        selling_plan: planId ? parseInt(planId, 10) : null,
        sections: this.getSectionsToRender().map((section) => section.id),
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => response.json())
      .then((state) => {
        if (state.errors || state.status) throw new Error(state.description || state.errors);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'rv-drawer', cartData: state });
        }
        this.renderContents(state);
        this.rvRefocus();
      })
      .catch(() => {
        group.removeAttribute('aria-busy');
        group.querySelectorAll('input, select').forEach((input) => (input.disabled = false));
        const error = document.getElementById(`CartDrawer-LineItemError-${line}`);
        const text = error && error.querySelector('.cart-item__error-text');
        if (text) text.textContent = this.dataset.rvError || window.cartStrings.error;
      });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    this.rvEnsureTrap(); // Rhovive: focus guards (also after a re-render while already open)
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    // Rhovive: with reduced motion there is no transition, so no transitionend; a timer traps focus instead.
    let focusTrapped = false;
    const trapOnce = () => {
      if (focusTrapped || !this.classList.contains('active')) return;
      focusTrapped = true;
      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    };
    this.addEventListener('transitionend', trapOnce, { once: true });
    setTimeout(trapOnce, 450);

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // Rhovive: keep <cart-drawer>'s is-empty class true to the markup just rendered, whoever called us,
    // and put the focus guards back (#CartDrawer's markup was just replaced).
    this.classList.toggle('is-empty', Boolean(this.querySelector('.drawer__inner-empty')));
    this.rvEnsureTrap();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
